import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../Store';

// TYPES

import {
    TCasinoMenu,
    TDeviceInfo,
    TFixesStateData,
    TGamePerformance,
    TGlobalStyles,
    TInGameHistoryPage,
    TMaitananceModeData,
    TPopClient,
    TPopupMessage,
    TPopupPanel,
    TUiState
} from './AppState.types';

import { EGameStates } from '@Managers/StateManager/GameStates';

import { EGameStoreKeys, TUILayerGameStoreSchema } from '@GameStore/GameStoreSchema';
// INITIAL STATE
import { initialState } from './AppState.initialState';

import LocalStorageItem from '@Managers/Storage/LocalStorageItem';
import LocalStorageManager from '@Managers/Storage/LocalStorageManager';
import { LocalStorageItemKeys } from '@Managers/Storage/LocalStorageManager.types';

export const appState = createSlice({
    name: 'app',
    initialState,
    reducers: {
        setStoreUpdate: (state, action: PayloadAction<{ payload: TUILayerGameStoreSchema }>) => {
            state.gameStore = action.payload.payload;
        },
        setInitState: (state, action: PayloadAction<boolean>) => {
            state.init = action.payload;
        },
        setCanvasReady: (state, action: PayloadAction<boolean>) => {
            state.canvasReady = action.payload;
        },
        setReadyState: (state, action: PayloadAction<boolean>) => {
            state.readyState = action.payload;
        },
        setMaintananceMode: (state, action: PayloadAction<TMaitananceModeData>) => {
            if (action.payload.active) {
                state.maintananceMode.active = true;
            } else {
                if (action.payload.event === 'Maintenance' && action.payload.startAt) {
                    const utcToLocalDate = new Date(action.payload.startAt); //  date format '2025-11-23T:50:00.000Z';
                    const timeNow = new Date();

                    const difference = utcToLocalDate.getTime() - timeNow.getTime();
                    const differenceSeconds = Math.floor((difference / 1000) % 60);
                    const differenceMinute = Math.floor((difference / (1000 * 60)) % 60);
                    const differenceHours = Math.floor((difference / (1000 * 60 * 60)) % 24);

                    state.maintananceMode.time = { h: differenceHours, m: differenceMinute, s: differenceSeconds };
                }
            }
        },
        setLogOut: (state, action: PayloadAction<boolean>) => {
            state.loggedOut = action.payload;
            state.init = false;
        },
        setPopupPanel: (state, action: PayloadAction<false | TPopupPanel>) => {
            state.popupPanel = state.popupPanel === action.payload ? false : action.payload;

            if (state.popupPanel && state.menuPages) {
                state.menuPages = false;
            }
        },
        setLeftHandMode: (state, action: PayloadAction<any>) => {
            state.leftHandMode = action.payload;
            LocalStorageManager.saveItem(new LocalStorageItem(LocalStorageItemKeys.LEFT_HAND_MODE, JSON.stringify(action.payload)));
        },
        setPopupMessage: (state, action: PayloadAction<TPopupMessage>) => {
            state.popupMessage = action.payload;
        },
        updateDeviceDimensions: (state, action: PayloadAction<TDeviceInfo>) => {
            state.device = { ...state.device, ...action.payload };
        },
        setCasinoMenu: (state, action: PayloadAction<Partial<TCasinoMenu>>) => {
            state.casinoMenu = { ...state.casinoMenu, ...action.payload };
        },
        setCurrentGameState: (state, action: PayloadAction<EGameStates>) => {
            state.currentGameState = action.payload;
        },
        setWCAMode: (state, action: PayloadAction<boolean>) => {
            state.wcaMode = action.payload;
            LocalStorageManager.saveItem(new LocalStorageItem(LocalStorageItemKeys.WCA_MODE, JSON.stringify(action.payload)));
        },
        setFixesState: (state, action: PayloadAction<Partial<TFixesStateData>>) => {
            if (action.payload.fixCombinations?.length === 0) {
                state.fixesState = { ...state.fixesState, ...action.payload, currentIndex: undefined, active: false };
            } else {
                state.fixesState = { ...state.fixesState, ...action.payload };
            }
        },
        setGamePerformance: (state, action: PayloadAction<TGamePerformance>) => {
            state.gamePerformance = action.payload;
        },
        setGlobalStyles: (state, action: PayloadAction<Partial<TGlobalStyles>>) => {
            state.globalStyles = { ...state.globalStyles, ...action.payload };
        },
        setDebugTools: (state, action: PayloadAction<boolean>) => {
            state.debugTools = action.payload;
        },
        setMenuPages: (state, action: PayloadAction<boolean>) => {
            state.menuPages = action.payload;

            if (state.menuPages && state.popupPanel) {
                state.popupPanel = false;
            }
        },
        setPopClient: (state, action: PayloadAction<Partial<TPopClient>>) => {
            state.popClient = { ...state.popClient, ...action.payload };
        },
        setUIState: (state, action: PayloadAction<Partial<TUiState>>) => {
            state.uiState = { ...state.uiState, ...action.payload };
        },
        setInGameHistoryPage: (state, action: PayloadAction<TInGameHistoryPage>) => {
            state.inGameHistoryPage = action.payload;
        },
        setInGameHistoryMode: (state, action: PayloadAction<boolean>) => {
            state.inGameHistoryMode = action.payload;
        },
        setGameStoreKey: (
            state,
            action: PayloadAction<{
                key: EGameStoreKeys;
                payload: unknown;
            }>
        ) => {
            (state.gameStore as any)[action.payload.key] = action.payload.payload;
        }
    }
});

export const {
    setInitState,
    setMaintananceMode,
    setLogOut,
    setStoreUpdate,
    setReadyState,
    setCanvasReady,
    setPopupPanel,
    updateDeviceDimensions,
    setPopupMessage,
    setLeftHandMode,
    setCurrentGameState,
    setCasinoMenu,
    setWCAMode,
    setFixesState,
    setGamePerformance,
    setGlobalStyles,
    setDebugTools,
    setMenuPages,
    setPopClient,
    setUIState,
    setInGameHistoryPage,
    setInGameHistoryMode,
    setGameStoreKey
} = appState.actions;

// REDUCER METHODS

// GET STATUS VARS
export const getLoggedOut = (state: RootState) => state.app.loggedOut;
export const getReadyState = (state: RootState) => state.app.readyState;
export const getCanvasReadyState = (state: RootState) => state.app.canvasReady;
export const getGameStore = (state: RootState) => state.app.gameStore;
export const getAccount = (state: RootState) => state.app.gameStore[EGameStoreKeys.Account];
export const getUIMaxCoverage = (state: RootState) => state.app.gameStore[EGameStoreKeys.UIMaxScreenCoverage];
export const getPreloaderState = (state: RootState) => state.app.gameStore[EGameStoreKeys.PreloaderState];
export const getGameHistoryState = (state: RootState) => state.app.gameStore[EGameStoreKeys.GameHistoryState];
export const getFullScreen = (state: RootState) => state.app.gameStore[EGameStoreKeys.IsFullscreen];
export const getButtonState = (state: RootState) => state.app.gameStore[EGameStoreKeys.ButtonState];
export const getMenuButtonActive = (state: RootState) => state.app.gameStore[EGameStoreKeys.ButtonState].isMenuButtonActive;
export const getMuteButtonActive = (state: RootState) => state.app.gameStore[EGameStoreKeys.ButtonState].isMuteButtonActive;
export const getUnmuteButtonActive = (state: RootState) => state.app.gameStore[EGameStoreKeys.ButtonState].isUnmuteButtonActive;
export const getTurboModeState = (state: RootState) => state.app.gameStore[EGameStoreKeys.TurboModeState];
export const getBetState = (state: RootState) => state.app.gameStore[EGameStoreKeys.BetState];
export const getNumberOfReels = (state: RootState) => state.app.gameStore[EGameStoreKeys.NumberOfReels];
export const getNumberOfSymbolsPerReel = (state: RootState) => state.app.gameStore[EGameStoreKeys.NumberOfSymbolsPerReel];
export const getAutoPlayState = (state: RootState) => state.app.gameStore[EGameStoreKeys.AutoPlayState];
export const getGeneralAppState = (state: RootState) => state.app.gameStore[EGameStoreKeys.GeneralAppState];
export const getCurrentGameState = (state: RootState) => state.app.gameStore[EGameStoreKeys.CurrentGameState];
export const getCurrentGameSubState = (state: RootState) => state.app.gameStore[EGameStoreKeys.CurrentGameSubState];
export const getFreeSpins = (state: RootState) => state.app.gameStore[EGameStoreKeys.Account].bonuses.freeSpins;
export const getGamePlayMessages = (state: RootState) => state.app.gameStore[EGameStoreKeys.GamePlayMessages];
export const getDebugWinPresentationModeState = (state: RootState) => state.app.gameStore[EGameStoreKeys.DebugWinPresentationModeState];
export const getDebugWinPresentationModeStateAvailable = (state: RootState) => state.app.gameStore[EGameStoreKeys.DebugWinPresentationModeState].available;
export const getFeatures = (state: RootState) => state.app.gameStore[EGameStoreKeys.Features];
export const getOfAKind = (state: RootState) => state.app.gameStore[EGameStoreKeys.Features].ofAKind;
export const getAuthenticateGameConfig = (state: RootState) => state.app.gameStore[EGameStoreKeys.AuthenticateGameConfig];
export const getEnvironmentConfiguration = (state: RootState) => state.app.gameStore[EGameStoreKeys.EnvironmentConfiguration];
export const getSymbolConfiguration = (state: RootState) => state.app.gameStore[EGameStoreKeys.SymbolConfiguration];
export const getSoundSettings = (state: RootState) => state.app.gameStore[EGameStoreKeys.SoundSettings];
export const getMaintananceMode = (state: RootState) => state.app.maintananceMode;
export const getPopupPanel = (state: RootState) => state.app.popupPanel;
export const getDeviceInfo = (state: RootState) => state.app.device;
export const getLeftHandMode = (state: RootState) => state.app.leftHandMode;
export const getPopupMessges = (state: RootState) => state.app.popupMessage;
export const getCasinoMenu = (state: RootState) => state.app.casinoMenu;
export const getWCAMode = (state: RootState) => state.app.wcaMode;
export const getFixesState = (state: RootState) => state.app.fixesState;
export const getPerformanceData = (state: RootState) => state.app.gamePerformance;
export const getActualFps = (state: RootState) => state.app.gamePerformance.actualFps;
export const getGlobalStyles = (state: RootState) => state.app.globalStyles;
export const getOrientation = (state: RootState) => state.app.globalStyles.orientation;
export const getDebugTools = (state: RootState) => state.app.debugTools;
export const getMenuPages = (state: RootState) => state.app.menuPages;
export const getPopClient = (state: RootState) => state.app.popClient;
export const getUIState = (state: RootState) => state.app.uiState;
export const getInGameHistoryPage = (state: RootState) => state.app.inGameHistoryPage;
export const getInGameHistoryMode = (state: RootState) => state.app.inGameHistoryMode;
export const getBonusBuy = (state: RootState) => state.app.gameStore[EGameStoreKeys.BonusBuy];

export const appReducer = appState.reducer;
